/**
 * Guardas de rota. O servidor é quem autoriza; aqui só se decide o que
 * mostrar — e nunca se mostra um portal para um contexto que não é dele.
 */
import { Center, Loader } from '@mantine/core';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { podeEntrarNoPortal, portalFor, useBrand, useSession, type PortalId } from '@eva/domain';
import { orientaProfissionais } from '../pages/CriarConta';
import { CADASTRO } from '../cadastro/rotas';
import type { ReactNode } from 'react';

const REDIRECT_KEY = 'eva_app_redirect_after_login';

export function rememberRedirect(path: string) {
  try {
    sessionStorage.setItem(REDIRECT_KEY, path);
  } catch {
    /* sem storage */
  }
}

/**
 * O endereço que pediu login, se houver. Só caminho interno do app: `//x` ou
 * `/\\x` seriam outro site para o navegador, e isto não pode virar
 * redirecionador aberto.
 *
 * Lê SEM consumir. O endereço só sai do storage quando a pessoa chega nele
 * (`RequireSession`). Consumir na hora de navegar deixava duas navegações
 * disputando o pós-login — o `navigate` do botão e o redesenho da tela de
 * login com a sessão pronta —, e quem chegava por último ganhava. Em QA, com
 * o portal demorando a carregar, a tela inicial ganhava e o consentimento do
 * conector MCP se perdia (26/09/2026).
 */
export function peekRedirect(): string | null {
  try {
    const v = sessionStorage.getItem(REDIRECT_KEY);
    return v && v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') ? v : null;
  } catch {
    return null;
  }
}

function esquecerRedirect() {
  try {
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    /* sem storage */
  }
}

/** Para onde ir depois do login: o endereço que pediu login, ou a casa do contexto. */
export function destinoDepoisDoLogin(role: Parameters<typeof portalFor>[0]): string {
  return peekRedirect() ?? homeFor(role);
}

function Carregando() {
  return (
    <Center h="100dvh">
      <Loader color="brand" />
    </Center>
  );
}

/** Para onde uma sessão pronta vai: o portal do contexto, ou o legado. */
export function homeFor(role: Parameters<typeof portalFor>[0]): string {
  const portal = portalFor(role);
  return portal.available ? portal.path : '/legado';
}

/**
 * A primeira tela de quem chega sem sessão (A083): na marca de porta aberta
 * e sem rede de profissionais, o cadastro — quem chega pela primeira vez é,
 * quase sempre, quem ainda não tem conta, e quem já tem entra pelo "Já possui
 * cadastro? Fazer login" (ou pela própria senha, no cadastro). Nas outras, a
 * tela de entrar, que separa paciente de profissional e explica a porta
 * fechada.
 *
 * Só a raiz muda: `/entrar` continua sendo a tela de entrar, para onde vão o
 * sair, a sessão vencida e os links dos e-mails — quem passa por ali já tem
 * conta.
 */
export function primeiraTela(brand: { cadastroSoComLink?: boolean | null; enabledFeatures?: unknown; appUiPreferences?: unknown }): string {
  return !brand.cadastroSoComLink && !orientaProfissionais(brand) ? CADASTRO.email : '/entrar';
}

/** `/`: decide pelo estado da sessão. */
export function EntryGate() {
  const s = useSession();
  const { brand } = useBrand();
  if (s.status === 'loading') return <Carregando />;
  if (s.status === 'anonymous') return <Navigate to={primeiraTela(brand)} replace />;
  if (s.status === 'choosing') return <Navigate to="/contextos" replace />;
  return <Navigate to={destinoDepoisDoLogin(s.context!.role)} replace />;
}

export function RequireSession() {
  const s = useSession();
  const location = useLocation();
  const aqui = location.pathname + location.search;
  // Chegou onde o login tinha sido pedido: agora sim o pedido sai.
  useEffect(() => {
    if ((s.status === 'ready' || s.status === 'choosing') && peekRedirect() === aqui) esquecerRedirect();
  }, [s.status, aqui]);
  if (s.status === 'loading') return <Carregando />;
  if (s.status === 'anonymous') {
    rememberRedirect(aqui);
    return <Navigate to="/entrar" replace />;
  }
  return <Outlet />;
}

/** Só renderiza o portal se o contexto assumido for dele. */
export function RequirePortal({ portal, children }: { portal: PortalId; children: ReactNode }) {
  const s = useSession();
  if (s.status === 'choosing') return <Navigate to="/contextos" replace />;
  if (s.status !== 'ready' || !s.context) return <Carregando />;
  // Cada papel no portal do próprio vínculo — o mantenedor inclusive, que
  // desde a A024 não administra marca.
  if (!podeEntrarNoPortal(s.context.role, portal)) {
    return <Navigate to={homeFor(s.context.role)} replace />;
  }
  // Conta com onboarding pendente (A062, fase C) não entra no app da
  // paciente: sem momento escolhido, ela seria lida como gestante.
  if (portal === 'eu' && (s.user as any)?.onboardingPendente === true) {
    return <Navigate to="/cadastro/onboarding" replace />;
  }
  return <>{children}</>;
}
